import * as React from 'react';
import { AxiosError } from 'axios';
import { FleetData } from 'App.types';
import captainFleetData from 'pages/FeatureFlag/FeatureFlagFleets/fleets.json';
import { apiUrl } from 'utils/apiUrl';
import useRestApi from './useRestApi';

export interface CatalogFleet {
  id: string;
  name: string;
  region: string;
  source: 'captain' | 'houston';
}

export interface UseFleetCatalogResult {
  fleets: CatalogFleet[];
  loading: boolean;
  // Set when the Houston fleet registry could not be fetched. Captain fleets
  // are still returned so consumers can degrade gracefully.
  houstonError: AxiosError | undefined;
  fetch: () => Promise<unknown>;
}

/**
 * Returns the combined fleet catalog: legacy Captain fleets (hardcoded in
 * fleets.json; no API exists and the set is frozen) plus Houston fleets
 * fetched live from the tenancy fleet registry.
 */
export function useFleetCatalog(): UseFleetCatalogResult {
  const { data, error, loading, fetch } = useRestApi<{ items: FleetData[] }>(
    apiUrl('tenancy', 'fleets'),
  );

  const fleets = React.useMemo<CatalogFleet[]>(() => {
    const captainFleets = captainFleetData.map<CatalogFleet>((fleet) => ({
      id: fleet.id.toString(),
      name: fleet.name,
      region: fleet.region.toLowerCase(),
      source: 'captain',
    }));
    const houstonFleets = (data?.items ?? [])
      .filter((fleet) => !fleet.name.startsWith('Integration Test Fleet:'))
      .map<CatalogFleet>((fleet) => ({
        id: fleet.fleetId,
        name: fleet.fleetId,
        region: fleet.region.toLowerCase(),
        source: 'houston',
      }));
    return [...captainFleets, ...houstonFleets];
  }, [data]);

  return { fleets, loading, houstonError: error, fetch };
}

export default useFleetCatalog;
